/* eslint-disable @stylistic/quotes */
import type { Config } from 'tailwindcss'
import plugin from 'tailwindcss/plugin'
import typo from '@tailwindcss/typography'
import queries from '@tailwindcss/container-queries'
import defaultTheme from 'tailwindcss/defaultTheme'
import headlessui from '@headlessui/tailwindcss'

function pxToRem(px: number): string {
  return `${+(px / 16).toFixed(3)}rem`
}

export default {
  content: [
    './components/**/*.{vue,js,ts}',
    './layouts/**/*.vue',
    './pages/**/*.vue',
    './composables/**/*.{js,ts}',
    './plugins/**/*.{js,ts}',
    './utils/**/*.{js,ts}',
    './App.{js,ts,vue}',
    './app.{js,ts,vue}',
    './Error.{js,ts,vue}',
    './error.{js,ts,vue}',
    './app.config.{js,ts}',
  ],
  safelist: [
    'typo-display-3xl',
    'typo-display-2xl',
    'typo-display-xl',
    'typo-display-lg',
    'typo-heading-3xl',
    'typo-heading-2xl',
    'typo-heading-xl',
    'typo-heading-lg',
    'typo-heading-md',
    'typo-heading-sm',
    'typo-body-4xl',
    'typo-body-3xl',
    'typo-body-2xl',
    'typo-body-xl',
    'typo-body-xl-fat',
    'typo-body-lg',
    'typo-body-md',
    'typo-button-text-xl',
    'typo-button-text-sm',
    'typo-label-lg',
    'typo-label-md',
  ],

  theme: {
    screens: {
      'xs': '448px',
      'sm': '640px',
      'md': '768px',
      'lg': '1024px',
      'xl': '1280px',
      '2xl': '1536px',
      '3xl': '1920px',
      '4xl': '2560px',
    },

    fontFamily: {
      sans: ['"Avenir Next LT Pr"', ...defaultTheme.fontFamily.sans],
    },

    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      inherit: 'inherit',

      white: '#fff',
      black: '#000',
      dark: '#0B152C',

      theme: {
        50: "var(--color-50,  theme('colors.blue.50'))",
        100: "var(--color-100,  theme('colors.blue.100'))",
        200: "var(--color-200,  theme('colors.blue.200'))",
        300: "var(--color-300,  theme('colors.blue.300'))",
        400: "var(--color-400,  theme('colors.blue.400'))",
        500: "var(--color-500,  theme('colors.blue.500'))",
        600: "var(--color-600,  theme('colors.blue.600'))",
        text: "var(--color-text,  theme('colors.dark'))",
      },
      blue: {
        50: '#CCDEEE',
        100: '#AAC9E2',
        200: '#80AED4',
        300: '#5592C6',
        400: '#2A77B7',
        500: '#005CA9',
        600: '#004D8D',
        700: '#003D71',
      },
      grey: {
        100: '#F3F7FF',
        200: '#CCD7EE',
      },
      bordeaux: {
        50: '#E5D4DD',
        100: '#DAB7C6',
        200: '#CF9FB8',
        300: '#A96E8C',
        400: '#944A70',
        500: '#7E2653',
        600: '#692045',
      },
      kiwi: {
        50: '#ECF4D7',
        100: '#E0ECBD',
        200: '#D0E39C',
        300: '#C0D97B',
        400: '#B1D05A',
        500: '#A1C639',
        600: '#86A530',
      },
      sand: {
        50: '#F7F4E1',
        100: '#F2ECCC',
        200: '#ECE3B3',
        300: '#E5D99A',
        400: '#DFD080',
        500: '#D8C667',
        600: '#B4A556',
      },
      turquoise: {
        50: '#E0EFF4',
        100: '#CBE4ED',
        200: '#B1D7E5',
        300: '#96C9DC',
        400: '#7CBCD3',
        500: '#62AECA',
        600: '#5291A8',
      },
    },

    fontSize: {
      // DISPLAY
      'display-3xl': [
        pxToRem(90),
        {
          lineHeight: pxToRem(100),
        },
      ],
      'display-2xl': [
        pxToRem(80),
        {
          lineHeight: pxToRem(90),
        },
      ],
      'display-xl': [
        pxToRem(65),
        {
          lineHeight: pxToRem(70),
        },
      ],
      'display-lg': [
        pxToRem(60),
        {
          lineHeight: pxToRem(65),
        },
      ],
      // HEADING
      'heading-3xl': [
        pxToRem(45),
        {
          lineHeight: pxToRem(60),
        },
      ],
      'heading-2xl': [
        pxToRem(40),
        {
          lineHeight: pxToRem(50),
        },
      ],
      'heading-xl': [
        pxToRem(30),
        {
          lineHeight: pxToRem(40),
        },
      ],
      'heading-lg': [
        pxToRem(26),
        {
          lineHeight: pxToRem(36),
        },
      ],
      'heading-md': [
        pxToRem(22),
        {
          lineHeight: pxToRem(28),
        },
      ],
      'heading-sm': [
        pxToRem(20),
        {
          lineHeight: pxToRem(28),
        },
      ],
      // BODY
      'body-4xl': [
        pxToRem(30),
        {
          lineHeight: pxToRem(40),
        },
      ],
      'body-3xl': [
        pxToRem(22),
        {
          lineHeight: pxToRem(34),
        },
      ],
      'body-2xl': [
        pxToRem(18),
        {
          lineHeight: pxToRem(30),
        },
      ],
      'body-xl': [
        pxToRem(16),
        {
          lineHeight: pxToRem(24),
        },
      ],
      'body-xl-fat': [
        pxToRem(16),
        {
          fontWeight: '600',
          lineHeight: pxToRem(24),
        },
      ],
      'body-lg': [
        pxToRem(14),
        {
          lineHeight: pxToRem(20),
        },
      ],
      'body-md': [
        pxToRem(12),
        {
          lineHeight: pxToRem(18),
        },
      ],
      // BUTTON TEXT
      'button-text-xl': [
        pxToRem(18),
        {
          lineHeight: pxToRem(32),
          letterSpacing: '0',
        },
      ],
      'button-text-sm': [
        pxToRem(14),
        {
          lineHeight: pxToRem(18),
          letterSpacing: '0',
        },
      ],
      // LABEL
      'label-lg': [
        pxToRem(16),
        {
          lineHeight: pxToRem(22),
          letterSpacing: '0.25em',
        },
      ],
      'label-md': [
        pxToRem(14),
        {
          lineHeight: pxToRem(20),
          letterSpacing: '0.25em',
        },
      ],
    },

    extend: {
      spacing: {

        // Desktop navigation padding top for filling nav spacing
        'navbar-height': 'var(--navbar-height)',

        // containers
        'container-text': '55rem',
        'container-max': '100rem',
        'container': '1.375rem',
        'container-desktop': '6.25rem',

        'container-lg': '2.625rem',
        'container-lg-desktop': '11rem',

        'title': '55ch',
        'text': '75ch',

        'inherit': 'inherit',
        '4.5': '1.125rem', // 18px
        '5.5': '1.375rem', // 22px
        '6.5': '1.625rem', // 26px
        '18': '4.5rem', // 72px
        '22': '5.5rem', // 88px
        '26': '6.5rem', // 104px
      },

      flexGrow: {
        0.5: '0.5',
        2: '2',
        4: '4',
      },
      flexShrink: {
        0.5: '0.5',
        2: '2',
        4: '4',
      },

      borderWidth: {
        1.5: '1.5px',
        2: '2px',
      },
      borderRadius: {
        corner: 'var(--rounded-corner-size-directive)',
        100: '6.25rem',
      },

    },
  },

  plugins: [
    typo,
    queries,
    headlessui,

    plugin(function ({ addVariant, addUtilities, theme }) {
      addVariant('touch', '.is-touch &')
      addVariant('no-touch', '.no-touch &')
      addVariant('hover-focus', ['&:hover', '&:focus-within'])
      addVariant('group-hover-focus', [
        '.group:hover &',
        '.group:focus-within &',
      ])
      addVariant('peer-hover-focus', [
        '.peer:hover ~ &',
        '.peer:focus-within ~ &',
      ])
      addUtilities({
        '.typo': {
          // DISPLAY
          '&-display-2xl': {
            'font-size': theme('fontSize.display-2xl[0]'), // First item: font size
            'line-height': theme('fontSize.display-2xl[1].lineHeight'), // Nested lineHeight
            'max-width': theme('spacing.title'), // From your spacing.title
            'text-wrap': 'pretty',
            'white-space': 'pre-wrap',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto', // Direct CSS instead of @apply
            },
          },
          '&-display-xl': {
            'font-size': theme('fontSize.display-xl[0]'),
            'line-height': theme('fontSize.display-xl[1].lineHeight'),
            'max-width': theme('spacing.title'),
            'text-wrap': 'pretty',
            'white-space': 'pre-wrap',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto',
            },
          },
          '&-display-lg': {
            'font-size': theme('fontSize.display-lg[0]'),
            'line-height': theme('fontSize.display-lg[1].lineHeight'),
            'max-width': theme('spacing.title'),
            'text-wrap': 'pretty',
            'white-space': 'pre-wrap',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto',
            },
          },
          // HEADING
          '&-heading-3xl': {
            'font-size': theme('fontSize.heading-3xl[0]'),
            'line-height': theme('fontSize.heading-3xl[1].lineHeight'),
            'max-width': theme('spacing.title'),
            'text-wrap': 'pretty',
            'white-space': 'pre-wrap',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto',
            },
          },
          '&-heading-2xl': {
            'font-size': theme('fontSize.heading-2xl[0]'),
            'line-height': theme('fontSize.heading-2xl[1].lineHeight'),
            'max-width': theme('spacing.title'),
            'text-wrap': 'pretty',
            'white-space': 'pre-wrap',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto',
            },
          },
          '&-heading-xl': {
            'font-size': theme('fontSize.heading-xl[0]'),
            'line-height': theme('fontSize.heading-xl[1].lineHeight'),
            'max-width': theme('spacing.title'),
            'font-weight': '500',
            'text-wrap': 'pretty',
            'white-space': 'pre-wrap',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto',
            },
          },
          '&-heading-lg': {
            'font-size': theme('fontSize.heading-lg[0]'),
            'line-height': theme('fontSize.heading-lg[1].lineHeight'),
            'max-width': theme('spacing.title'),
            'font-weight': '500',
            'text-wrap': 'pretty',
            'white-space': 'pre-wrap',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto',
            },
          },
          '&-heading-md': {
            'font-size': theme('fontSize.heading-md[0]'),
            'line-height': theme('fontSize.heading-md[1].lineHeight'),
            'max-width': theme('spacing.title'),
            'font-weight': '500',
            'white-space': 'pre-wrap',
            'text-wrap': 'pretty',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto',
            },
          },
          '&-heading-sm': {
            'font-size': theme('fontSize.heading-sm[0]'),
            'line-height': theme('fontSize.heading-sm[1].lineHeight'),
            'max-width': theme('spacing.title'),
            'font-weight': '500',
            'white-space': 'pre-wrap',
            'text-wrap': 'pretty',
            [`@media (max-width: ${theme('screens.xl')})`]: {
              hyphens: 'auto',
            },
          },
          // BODY
          '&-body-4xl': {
            'font-size': theme('fontSize.body-4xl[0]'),
            'line-height': theme('fontSize.body-4xl[1].lineHeight'),
            'max-width': theme('spacing.text'),
            'white-space': 'pre-wrap',
            'font-weight': '500',
          },
          '&-body-3xl': {
            'font-size': theme('fontSize.body-3xl[0]'),
            'line-height': theme('fontSize.body-3xl[1].lineHeight'),
            'max-width': theme('spacing.text'),
            'white-space': 'pre-wrap',
            'font-weight': '500',
          },
          '&-body-2xl': {
            'font-size': theme('fontSize.body-2xl[0]'),
            'line-height': theme('fontSize.body-2xl[1].lineHeight'),
            'max-width': theme('spacing.text'),
            'white-space': 'pre-wrap',
            'font-weight': '500',
          },
          '&-body-xl': {
            'font-size': theme('fontSize.body-xl[0]'),
            'line-height': theme('fontSize.body-xl[1].lineHeight'),
            'max-width': theme('spacing.text'),
            'white-space': 'pre-wrap',
            'font-weight': '500',
          },
          '&-body-xl-fat': {
            'font-size': theme('fontSize.body-xl-fat[0]'),
            'line-height': theme('fontSize.body-xl-fat[1].lineHeight'),
            'max-width': theme('spacing.text'),
            'white-space': 'pre-wrap',
            'font-weight': '600',
          },
          '&-body-lg': {
            'font-size': theme('fontSize.body-lg[0]'),
            'line-height': theme('fontSize.body-lg[1].lineHeight'),
            'max-width': theme('spacing.text'),
            'white-space': 'pre-wrap',
            'font-weight': '500',
          },
          '&-body-md': {
            'font-size': theme('fontSize.body-md[0]'),
            'line-height': theme('fontSize.body-md[1].lineHeight'),
            'max-width': theme('spacing.text'),
            'white-space': 'pre-wrap',
            'font-weight': '500',
          },
          // BUTTON TEXT
          '&-button-text-xl': {
            'font-size': theme('fontSize.button-text-xl[0]'),
            'line-height': theme('fontSize.button-text-xl[1].lineHeight'),
            'letter-spacing': theme('fontSize.button-text-xl[1].letterSpacing'),
            'white-space': 'pre-wrap',
            'font-weight': '500',
          },
          '&-button-text-sm': {
            'font-size': theme('fontSize.button-text-sm[0]'),
            'line-height': theme('fontSize.button-text-sm[1].lineHeight'),
            'letter-spacing': theme('fontSize.button-text-sm[1].letterSpacing'),
            'white-space': 'pre-wrap',
            'font-weight': '500',
          },
          // LABEL
          '&-label-lg': {
            'font-size': theme('fontSize.label-lg[0]'),
            'line-height': theme('fontSize.label-lg[1].lineHeight'),
            'letter-spacing': theme('fontSize.label-lg[1].letterSpacing'),
            'white-space': 'pre-wrap',
            'font-weight': '600',
            'text-transform': 'uppercase',
          },
          '&-label-md': {
            'font-size': theme('fontSize.label-md[0]'),
            'line-height': theme('fontSize.label-md[1].lineHeight'),
            'letter-spacing': theme('fontSize.label-md[1].letterSpacing'),
            'white-space': 'pre-wrap',
            'font-weight': '600',
            'text-transform': 'uppercase',
          },
        },
      })
    }),
  ],

} satisfies Config
